import { useState } from 'react';
import type { PayorResponse } from '@vagusx/clients';
import { SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { SheetActions, SheetBody } from '@/components/custom/sheet-layout';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, ChevronRight, Loader2 } from 'lucide-react';
import { useDebounce } from '@/hooks/use-debounce';
import { usePayorSearch, useAddAcceptedPayor } from '@/hooks/use-payors';

interface AcceptedPayorEditorProps {
  onClose: () => void;
}

export function AcceptedPayorEditor({ onClose }: AcceptedPayorEditorProps): React.ReactElement {
  const [search, setSearch] = useState('');

  const debouncedSearch = useDebounce(search, 300);
  const { data: searchResults, isLoading: isSearching } = usePayorSearch(debouncedSearch);
  const addAccepted = useAddAcceptedPayor();

  const handleAddPayor = async (payor: PayorResponse) => {
    await addAccepted.mutateAsync({ payorId: payor.id });
    onClose();
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <SheetHeader>
        <SheetTitle>Add Payor</SheetTitle>
        <SheetDescription>
          Search and select a payor to accept. All plans are enabled by default.
        </SheetDescription>
      </SheetHeader>

      <SheetBody>
        <div className="flex flex-col gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Type to search payors..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              autoFocus
            />
            {isSearching && (
              <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
            )}
          </div>

          {!search && (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Start typing to search the payor catalog
            </p>
          )}

          {search && debouncedSearch && (
            <div className="space-y-1">
              {searchResults?.map((payor) => (
                <button
                  key={payor.id}
                  type="button"
                  onClick={() => handleAddPayor(payor)}
                  disabled={addAccepted.isPending}
                  className="flex w-full items-center justify-between rounded-md px-3 py-2 text-sm hover:bg-accent disabled:opacity-50"
                >
                  <div className="text-left">
                    <div className="font-medium">{payor.name}</div>
                    <div className="text-xs text-muted-foreground">{payor.type}</div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </button>
              ))}
              {searchResults?.length === 0 && !isSearching && (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  No payors found for &ldquo;{search}&rdquo;
                </p>
              )}
            </div>
          )}
        </div>
      </SheetBody>

      <SheetActions>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
      </SheetActions>
    </div>
  );
}
